import { BrowserModule } from '@angular/platform-browser'
import { inject, NgModule, provideAppInitializer } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { CommonModule } from '@angular/common'
import { BrowserAnimationsModule } from '@angular/platform-browser/animations'
import { TableModule } from 'primeng/table'
import { PrimeNG } from 'primeng/config'
import Material from '@primeng/themes/material'
import {
  NgClientAuthModule,
  AuthorizationGuardService,
  AuthInterceptorService
} from 'ng-client-auth'

import {
  ODataConfiguration,
  SmODataConfiguration,
  TmODataConfiguration,
  TmrODataConfiguration
} from '@exchange/services/core/odata/config'
import { ClaimUpdateService } from './services/claimUpdateService'
import { ProcessingProgressService } from './services/processingProgressService'
import { SessionService, UtilService, UserPreferencesService } from '@exchange/services/.'

import {
  UserODataService,
  CompleteUserODataService,
  PmProviderODataService,
  RoleODataService,
  LogODataService,
  PmPayerODataService,
  ControlTooltipODataService,
  UserAdditionalDataODataService,
  UserProviderUnitODataService,
  ClaimAutomationODataService,
  ClaimAutomationElementsViewODataService,
  ClaimExportConfigODataService,
  ClaimExportElementODataService,
  ClaimExportProcessODataService,
  ClaimExportConfigViewODataService,
  TemplateSegmentODataService,
  ClaimExportODataService,
  FileViewModelODataService,
  Raw837FileExportODataService,
  ClaimExportElementViewODataService,
  ClaimLineViewODataService,
  ClaimLineExportTransformationService,
  ClaimLineExportODataService,
  ClaimLineX12TransformationService,
  ClaimLineX12ODataService,
  ClaimAdjustedReceivedDatesViewODataService,
  RemitLineViewODataService,
  ReadyToPayViewODataService,
  RemitLineProgressODataService,
  RemitLineExportTransformationService,
  RemitLineExportODataService,
  RemitLineX12TransformationService,
  RemitLineX12ODataService,
  PlanODataService,
  ClaimProcessingTypeODataService,
  ClaimProcessingRuleODataService,
  ProcedureCodeODataService,
  ProcedureCodesetODataService,
  ProcedureCodeFeeODataService,
  RevenueCodeODataService,
  RevenueCodeFeeODataService,
  LineOfBusinessODataService,
  PayerODataService,
  PayerAddressODataService,
  ProviderAddressODataService,
  PayerStateODataService,
  ProviderStateODataService,
  ProviderODataService,
  ProviderPayersODataService,
  ProviderContactsODataService,
  ProviderTypeODataService,
  ProviderCoveragesODataService,
  NpiTypeODataService,
  FacilityODataService,
  EntityGroupODataService,
  FeeScheduleODataService,
  MarketODataService,
  MarketChannelODataService,
  LocationCoverageTypeODataService,
  LocationCoverageStateInclusionODataService,
  EntityGroupPlanODataService,
  EntityGroupProviderODataService,
  EntityGroupFeeScheduleODataService,
  ClaimConfigurationItemODataService,
  EntityGroupPlanProviderViewODataService,
  ClaimConfigurationODataService,
  ClaimConfigurationItemTransformationTypeODataService,
  GridQuickFilterServiceDirective,
  PaymentService,
  PaymentODataService,
  BundleDefinitionODataService,
  RefDiagnosisRelatedGroupODataService,
  ProcessorDefinitionODataService,
  ProcessorDefinitionBlocklistODataService,
  ProcessorDefinitionValuelistODataService,
  PhoneTypeODataService,
  PhoneNumberODataService
} from '@exchange/domain/.'

import {
  NurseTypeODataService,
  PhysicianTypeODataService,
  UnitODataService
} from '@exchange/domain/lookups'

// Select
import { NgSelectModule } from '@ng-select/ng-select'

// tooltip
import { TooltipComponent, TooltipService } from '@exchange/ui/shared/controls'

// loading mask - busy
import { NgBusyModule } from 'ng-busy'

// diff
import { DiffComponent } from '@exchange/ui/shared/controls/diff/diff.component'

// ngx-bootstrap
import { AlertModule } from 'ngx-bootstrap/alert'
import { TypeaheadModule } from 'ngx-bootstrap/typeahead'
import { TabsModule } from 'ngx-bootstrap/tabs'
import { ModalModule } from 'ngx-bootstrap/modal'

// ag-grid
import { AgGridModule } from 'ag-grid-angular'
import { DatetimeComponent } from '@exchange/ui/shared/controls'

// Import Froala Editor.
import { FroalaEditorModule, FroalaViewModule } from 'angular-froala-wysiwyg'

// from component
import { EpisodeLinkRendererComponent } from '@exchange/ui/transactions/episode-link.renderer.component'
import { ClaimActionsRendererComponent } from '@exchange/ui/transactions/claim-actions.renderer.component'
import { RemitViewRendererComponent } from '@exchange/ui/transactions/remit-view.renderer.component'

// Quill Editor
import { QuillModule } from 'ngx-quill'

import { FileUploadModule } from 'ng2-file-upload'

import { appRoutes } from './app.routing'
import { AppComponent } from './app.component'
import { GridQuickFilterComponent } from '@exchange/ui/shared/controls/grid-quick-filter/grid-quick-filter.component'
import { UsersListComponent } from '@exchange/ui/admin/users/users-list/users-list.component'
import { PermissionsViewComponent } from '@exchange/ui/admin/permissions/permissions-view/permissions-view.component'
import { HeaderComponent } from '@exchange/ui/shared/header/header.component'
import { MenusComponent } from '@exchange/ui/shared/menus/menus.component'
import { IndexComponent } from '@exchange/ui/home/index/index.component'
import { TermsComponent } from '@exchange/ui/home/terms/terms.component'
import {
  ClaimAdjustedRecivedDateModalComponent,
  ClaimDeleteModalComponent,
  ClaimsListComponent
} from '@exchange/ui/transactions/claims-list/claims-list.component'
import {
  RemitDeleteModalComponent,
  RemitListComponent
} from '@exchange/ui/transactions/remit-list/remit-list.component'
import { RemitListMatchClaimModalComponent } from '@exchange/ui/transactions/remit-list/remit-list-match-claim-modal.component'
import {
  AuditDetailAddEntryModalComponent,
  AuditDetailComponent
} from '@exchange/ui/transactions/audit-detail/audit-detail.component'

import {
  PlansComponent,
  ProvidersComponent,
  ProvidersListComponent,
  ProvidersEditComponent,
  PlansListComponent,
  PlansEditComponent,
  PayersComponent,
  PayersListComponent,
  PayersEditComponent,
  BundleDefinitionComponent,
  BundleDefinitionListComponent,
  BundleDefinitionEditComponent,
  UploadersListComponent
} from '@exchange/ui/system'
import { AddressComponent } from '@exchange/ui/shared/controls/address/address.component'
import { ProvidersEditContactsComponent } from '@exchange/ui/system/providers-edit-contacts/providers-edit-contacts.component'
import { ProvidersEditDemographicsComponent } from '@exchange/ui/system/providers-edit-demographics/providers-edit-demographics.component'
import { ProvidersEditEdiConfigComponent } from '@exchange/ui/system/providers-edit-edi-config/providers-edit-edi-config.component'
import { EntityGroupListComponent } from '@exchange/ui/system/entity-group-list/entity-group-list.component'
import { EntityGroupComponent } from '@exchange/ui/system/entity-group/entity-group.component'
import { EntityGroupEditComponent } from '@exchange/ui/system/entity-group-edit/entity-group-edit.component'
import { EntityGroupEditPlansComponent } from '@exchange/ui/system/entity-group-edit-plans/entity-group-edit-plans.component'
import { EntityGroupEditProvidersComponent } from '@exchange/ui/system/entity-group-edit-providers/entity-group-edit-providers.component'
import { FeeScheduleComponent } from '@exchange/ui/system/fee-schedule/fee-schedule.component'
import { FeeScheduleListComponent } from '@exchange/ui/system/fee-schedule-list/fee-schedule-list.component'
import { FeeScheduleEditComponent } from '@exchange/ui/system/fee-schedule-edit/fee-schedule-edit.component'
import { MarketComponent } from '@exchange/ui/system/market/market.component'
import { MarketListComponent } from '@exchange/ui/system/market-list/market-list.component'
import { MarketEditComponent } from '@exchange/ui/system/market-edit/market-edit.component'
import { MarketEditProvidersComponent } from '@exchange/ui/system/market-edit-providers/market-edit-providers.component'
import { EntityGroupEditGeneralComponent } from '@exchange/ui/system/entity-group-edit-general/entity-group-edit-general.component'
import {
  EntityGroupEditConfigsComponent,
  EntityGroupEditCopyModalComponent,
  EntityGroupEditModalComponent
} from '@exchange/ui/system/entity-group-edit-configs/entity-group-edit-configs.component'
import { ClaimTransactionTypeODataService } from '@exchange/app/domain/system/claimTransactionType'
import { ResponseCodesClaimLineAdjustmentReasonsComponent } from '@exchange/ui/transactions/response-codes-claim-line-adjustment-reasons/response-codes-claim-line-adjustment-reasons.component'
import { ReadyToPayListComponent } from '@exchange/ui/transactions/payments/ready-to-pay-list/ready-to-pay-list.component'
import { PendingPaymentListComponent } from '@exchange/ui/transactions/payments/pending-payment-list/pending-payment-list.component'
import { PaymentsComponent } from '@exchange/ui/transactions/payments/payments.component'
import {
  PaymentDetailsComponent,
  PaymentDetailsModalComponent
} from '@exchange/ui/transactions/payments/payment-details/payment-details.component'
import { EntityGroupEditFeeschedulesComponent } from '@exchange/ui/system/entity-group-edit-feeschedules/entity-group-edit-feeschedules.component'
import { EntityGroupFeeScheduleCodesModalComponent } from '@exchange/ui/system/entity-group-edit-feeschedules/entity-group-fee-schedule-codes-modal.component'
import { EntityGroupEditFeescheduleTimelineComponent } from '@exchange/ui/system/entity-group-edit-feeschedule-timeline/entity-group-edit-feeschedule-timeline.component'
import { EntityGroupEditProcessingRulesComponent } from '@exchange/ui/system/entity-group-edit-processing-rules/entity-group-edit-processing-rules.component'
import {
  FeeScheduleEditCodesComponent,
  FeeScheduleEditCodesCopyModalComponent
} from '@exchange/ui/system/fee-schedule-edit-codes/fee-schedule-edit-codes.component'
import {
  FeeScheduleEditRevenueCodesComponent,
  FeeScheduleEditRevenueCodesCopyModalComponent
} from '@exchange/ui/system/fee-schedule-edit-revenue-codes/fee-schedule-edit-revenue-codes.component'
import {
  AddRemittanceRemarksModalComponent,
  ResponseCodesRemittanceRemarksComponent
} from '@exchange/ui/transactions/response-codes-remittance-remarks/response-codes-remittance-remarks.component'
import { ClaimLineRemittanceAdviceODataService } from '@exchange/app/domain/claimLineRemittanceAdvice'
import { ClaimLineServiceAdjustmentODataService } from '@exchange/app/domain/claimLineServiceAdjustment'

import { ToastrModule } from 'ngx-toastr'
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http'
import { environment } from '@exchange/environment'
import { StoreModule } from '@ngrx/store'
import { CommonComponentsModule } from 'common-components'
import { EffectsModule } from '@ngrx/effects'
import { ODataFilterService } from 'common-services'
import { ServiceWorkerModule } from '@angular/service-worker'
import { MarketEditCoverageComponent } from './ui/system/market-edit-coverage/market-edit-coverage.component'
import { InputMaskDirective } from './services/inputMask'
import { PayerContractAndSupportComponent } from './ui/system/payers/payers-edit/payer-contract-and-support/payer-contract-and-support.component'
import { PayerMediaODataService } from './domain/payerMedia'
import { PayerDocumentService } from './services/payer-document.service'
import { DownloadDocumentRendererComponent } from './ui/system/payers/payers-edit/payer-contract-and-support/download-document.renderer.component'
import { ProvidersEditCoveragesComponent } from './ui/system/providers-edit-coverages/providers-edit-coverages.component'
import { EpisodeTypeODataService } from './domain/system/episodeTypes'
import { EpisodeTypeRendererComponent } from './ui/system/providers-edit-coverages/episode-type.renderer.component'
import { PlanRendererComponent } from './ui/system/providers-edit-coverages/plan.renderer.component'
import { ProvidersCoverageComponent } from './ui/system/providers-edit-coverages/providers-coverage/providers-coverage.component'
import { OnlyDigitsDirective } from './services/onlyDigits'
import { RouterModule } from '@angular/router'
import { DefaultComponent } from './ui/shared/default/default.component'

@NgModule({
  declarations: [
    AppComponent,
    DatetimeComponent,
    EpisodeTypeRendererComponent,
    PlanRendererComponent,
    GridQuickFilterComponent,
    EpisodeLinkRendererComponent,
    ClaimActionsRendererComponent,
    RemitViewRendererComponent,
    UsersListComponent,
    PermissionsViewComponent,
    UploadersListComponent,
    HeaderComponent,
    MenusComponent,
    DefaultComponent,
    IndexComponent,
    TermsComponent,
    TooltipComponent,
    DiffComponent,
    ClaimsListComponent,
    ClaimAdjustedRecivedDateModalComponent,
    ClaimDeleteModalComponent,
    RemitListComponent,
    RemitDeleteModalComponent,
    RemitListMatchClaimModalComponent,
    AuditDetailComponent,
    AuditDetailAddEntryModalComponent,
    PlansComponent,
    PlansListComponent,
    PlansEditComponent,
    ProvidersComponent,
    ProvidersListComponent,
    ProvidersEditComponent,
    PayersComponent,
    PayersListComponent,
    PayersEditComponent,
    AddressComponent,
    ProvidersEditContactsComponent,
    ProvidersEditDemographicsComponent,
    ProvidersEditEdiConfigComponent,
    ProvidersEditCoveragesComponent,
    ProvidersCoverageComponent,
    EntityGroupListComponent,
    EntityGroupComponent,
    EntityGroupEditComponent,
    EntityGroupEditPlansComponent,
    EntityGroupEditProvidersComponent,
    FeeScheduleComponent,
    FeeScheduleListComponent,
    FeeScheduleEditComponent,
    MarketComponent,
    MarketListComponent,
    MarketEditComponent,
    MarketEditProvidersComponent,
    MarketEditCoverageComponent,
    EntityGroupEditGeneralComponent,
    EntityGroupEditConfigsComponent,
    EntityGroupEditModalComponent,
    EntityGroupEditCopyModalComponent,
    ResponseCodesClaimLineAdjustmentReasonsComponent,
    ReadyToPayListComponent,
    PendingPaymentListComponent,
    PaymentsComponent,
    PaymentDetailsComponent,
    PaymentDetailsModalComponent,
    EntityGroupEditFeeschedulesComponent,
    EntityGroupEditFeescheduleTimelineComponent,
    EntityGroupEditProcessingRulesComponent,
    FeeScheduleEditCodesComponent,
    FeeScheduleEditCodesCopyModalComponent,
    FeeScheduleEditRevenueCodesComponent,
    FeeScheduleEditRevenueCodesCopyModalComponent,
    ResponseCodesRemittanceRemarksComponent,
    AddRemittanceRemarksModalComponent,
    EntityGroupFeeScheduleCodesModalComponent,
    BundleDefinitionComponent,
    BundleDefinitionListComponent,
    BundleDefinitionEditComponent,
    InputMaskDirective,
    OnlyDigitsDirective,
    PayerContractAndSupportComponent,
    PayerContractAndSupportComponent,
    DownloadDocumentRendererComponent
  ],
  bootstrap: [AppComponent],
  imports: [
    RouterModule,
    BrowserModule,
    CommonModule,
    FormsModule,
    BrowserAnimationsModule,
    CommonComponentsModule,
    StoreModule.forRoot({}),
    EffectsModule.forRoot([]),
    ModalModule.forRoot(),
    NgClientAuthModule.forRoot({
      userManagerSettings: environment.auth,
      timeoutIntervalInMilliseconds: environment.timeoutIntervalInMilliseconds,
      outputOIDCDebugLogs: environment.outputOIDCDebugLogs
    }),
    appRoutes,
    NgSelectModule,
    TabsModule.forRoot(),
    AlertModule.forRoot(),
    TypeaheadModule.forRoot(),
    FileUploadModule,
    AgGridModule,
    TableModule,
    FroalaEditorModule.forRoot(),
    FroalaViewModule.forRoot(),
    ToastrModule.forRoot({
      timeOut: 5000,
      extendedTimeOut: 0,
      positionClass: 'toast-bottom-right',
      closeButton: true,
      maxOpened: 5,
      enableHtml: true,
      progressBar: true
    }),
    NgBusyModule,
    QuillModule.forRoot(),
    ServiceWorkerModule.register('./ngsw-worker.js', {
      enabled: environment.serviceWorkerEnabled,
      registrationStrategy: 'registerImmediately'
    })
  ],
  providers: [
    provideAppInitializer(() => {
      inject(PrimeNG).theme.set({
        preset: Material,
        options: {
          darkModeSelector: false
        }
      })
    }),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptorService,
      multi: true
    },
    ODataConfiguration,
    TmODataConfiguration,
    TmrODataConfiguration,
    SmODataConfiguration,
    ClaimUpdateService,
    ProcessingProgressService,
    UserODataService,
    CompleteUserODataService,
    PmProviderODataService,
    SessionService,
    UtilService,
    TooltipService,
    ControlTooltipODataService,
    UserPreferencesService,
    RoleODataService,
    LogODataService,
    PmPayerODataService,
    UserAdditionalDataODataService,
    UserProviderUnitODataService,
    NurseTypeODataService,
    PhysicianTypeODataService,
    UnitODataService,
    TemplateSegmentODataService,
    ClaimExportElementODataService,
    ClaimExportProcessODataService,
    ClaimExportConfigODataService,
    ClaimAutomationODataService,
    ClaimAutomationElementsViewODataService,
    ClaimExportConfigViewODataService,
    ClaimExportODataService,
    FileViewModelODataService,
    Raw837FileExportODataService,
    ClaimExportElementViewODataService,
    ClaimLineViewODataService,
    ClaimLineExportTransformationService,
    ClaimLineExportODataService,
    ClaimLineX12TransformationService,
    ClaimLineX12ODataService,
    ClaimLineRemittanceAdviceODataService,
    ClaimLineServiceAdjustmentODataService,
    ClaimAdjustedReceivedDatesViewODataService,
    RemitLineViewODataService,
    RemitLineExportTransformationService,
    RemitLineExportODataService,
    RemitLineX12TransformationService,
    RemitLineX12ODataService,
    RemitLineProgressODataService,
    ReadyToPayViewODataService,
    PaymentODataService,
    PaymentService,
    PayerODataService,
    PayerMediaODataService,
    PayerDocumentService,
    PayerAddressODataService,
    ProviderAddressODataService,
    PayerStateODataService,
    ProviderStateODataService,
    PlanODataService,
    ClaimProcessingTypeODataService,
    ClaimProcessingRuleODataService,
    ProcedureCodeODataService,
    ProcedureCodesetODataService,
    ProcedureCodeFeeODataService,
    RevenueCodeODataService,
    RevenueCodeFeeODataService,
    ProviderODataService,
    ProviderPayersODataService,
    ProviderContactsODataService,
    ProviderTypeODataService,
    ProviderCoveragesODataService,
    EpisodeTypeODataService,
    NpiTypeODataService,
    FacilityODataService,
    FeeScheduleODataService,
    MarketODataService,
    MarketChannelODataService,
    LocationCoverageTypeODataService,
    LocationCoverageStateInclusionODataService,
    EntityGroupODataService,
    EntityGroupPlanODataService,
    EntityGroupProviderODataService,
    EntityGroupFeeScheduleODataService,
    ClaimTransactionTypeODataService,
    ClaimConfigurationODataService,
    ClaimConfigurationItemODataService,
    ClaimConfigurationItemTransformationTypeODataService,
    EntityGroupPlanProviderViewODataService,
    LineOfBusinessODataService,
    AuthorizationGuardService,
    GridQuickFilterServiceDirective,
    BundleDefinitionODataService,
    RefDiagnosisRelatedGroupODataService,
    ODataFilterService,
    ProcessorDefinitionODataService,
    ProcessorDefinitionBlocklistODataService,
    ProcessorDefinitionValuelistODataService,
    PhoneNumberODataService,
    PhoneTypeODataService,
    provideHttpClient(withInterceptorsFromDi())
  ]
})
export class AppModule {}
